import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Radio } from "lucide-react";
import { Section } from "@/components/layout/Section";
import { supabase } from "@/integrations/supabase/client";
import { fadeUp, staggerContainer, viscousEase, viewportOnce, smoothSpring } from "@/lib/motion-variants";

interface TrendSignal {
  id: string;
  title: string;
  summary: string;
  source_name: string | null;
  category: string | null;
  source_url: string;
  ingested_at: string;
  hero_image_url: string | null;
}

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const days = Math.floor(diff / 86400000);
  if (days === 0) return "today";
  if (days === 1) return "yesterday";
  if (days < 7) return `${days}d ago`;
  if (days < 30) return `${Math.floor(days / 7)}w ago`;
  return `${Math.floor(days / 30)}mo ago`;
}

export function PulseTeaser() {
  const [signals, setSignals] = useState<TrendSignal[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const { data } = await supabase
        .from("trend_signals" as never)
        .select("id,title,summary,source_name,category,source_url,ingested_at,hero_image_url")
        .eq("status", "active")
        .order("ingested_at", { ascending: false })
        .order("relevance_score", { ascending: false })
        .limit(3);
      setSignals((data as unknown as TrendSignal[]) || []);
      setLoading(false);
    })();
  }, []);

  return (
    <Section id="pulse" className="border-t border-border/60">
      <motion.div variants={staggerContainer} initial="hidden" whileInView="visible" viewport={viewportOnce}>
        <motion.div
          variants={fadeUp}
          transition={viscousEase}
          className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10"
        >
          <div className="max-w-2xl">
            <div className="flex items-center gap-2 mb-3">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-primary" />
              </span>
              <span className="text-xs uppercase tracking-[0.2em] text-primary font-medium">
                The Pulse · live · updated daily
              </span>
              {signals[0] && (
                <span className="text-xs text-muted-foreground ml-2">
                  Last updated {timeAgo(signals[0].ingested_at)}
                </span>
              )}
            </div>
            <h2 className="font-serif text-display-sm md:text-display-md text-foreground text-pretty">
              The signals we're watching this week.
            </h2>
            <p className="text-body-md text-muted-foreground mt-4 max-w-xl">
              A live editorial feed of the marketing moves worth knowing about. Curated across the web, ranked
              by relevance to marketing leaders, rewritten in our voice.
            </p>
          </div>
          <Link
            to="/insights/pulse"
            className="inline-flex items-center gap-2 text-sm font-medium text-primary hover:underline underline-offset-4 self-start md:self-end"
          >
            Open the Pulse <ArrowUpRight className="w-4 h-4" />
          </Link>
        </motion.div>

        {loading ? (
          <div className="grid md:grid-cols-3 gap-6">
            {[...Array(3)].map((_, i) => (
              <div
                key={i}
                className="rounded-xl bg-background border border-border/20 h-52 animate-pulse"
              />
            ))}
          </div>
        ) : signals.length === 0 ? (
          <div className="text-center py-16 border border-border rounded-xl">
            <Radio className="w-6 h-6 text-muted-foreground mx-auto mb-3" />
            <p className="text-muted-foreground">
              The radar runs daily. Check the{" "}
              <Link to="/insights/pulse" className="text-primary hover:underline">
                Pulse
              </Link>{" "}
              for the latest.
            </p>
          </div>
        ) : (
          <div className="grid md:grid-cols-3 gap-6">
            {signals.map((signal, i) => (
              <motion.article
                key={signal.id}
                variants={fadeUp}
                transition={{ ...smoothSpring, delay: i * 0.08 }}
                className="group flex flex-col bg-background border border-border/30 rounded-xl overflow-hidden hover:border-primary/40 transition-colors h-full"
              >
                {signal.hero_image_url && (
                  <a
                    href={signal.source_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block aspect-video overflow-hidden bg-muted/40"
                  >
                    <img
                      src={signal.hero_image_url}
                      alt=""
                      loading="lazy"
                      referrerPolicy="no-referrer"
                      className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500"
                      onError={(e) => {
                        (e.currentTarget.parentElement as HTMLElement).style.display = "none";
                      }}
                    />
                  </a>
                )}
                <div className="flex flex-col flex-1 p-6">
                  <div className="flex items-center gap-2 mb-3 text-[11px] uppercase tracking-wider text-muted-foreground">
                    {signal.category && (
                      <span className="text-primary font-medium">{signal.category}</span>
                    )}
                    <span>·</span>
                    <span>{timeAgo(signal.ingested_at)}</span>
                  </div>
                  <a
                    href={signal.source_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block flex-1"
                  >
                    <h3 className="font-serif text-heading-md text-foreground mb-3 leading-snug group-hover:text-primary transition-colors line-clamp-3">
                      {signal.title}
                    </h3>
                    <p className="text-body-md text-muted-foreground leading-relaxed line-clamp-3">
                      {signal.summary}
                    </p>
                  </a>
                  {signal.source_name && (
                    <p className="text-xs text-muted-foreground mt-4">{signal.source_name}</p>
                  )}
                </div>
              </motion.article>
            ))}
          </div>
        )}
      </motion.div>
    </Section>
  );
}
